export type ActivityId =
    | "kana"
    | "kanjiWriting"
    | "kanjiMemoryQuiz"
    | "lessons"
    | "grammar"
    | "vocabulary"
    | "conjugation"
    | "speaking";

export type ActivityDefinition = { id: ActivityId; features: string[]; to: string };

export type ActivityPeriod = "today" | "yesterday" | "week" | "month";

export type ActivitySummary = {
    period: ActivityPeriod;
    activities: ActivityId[];
    primary: ActivityDefinition;
    kind: "single" | "few" | "many";
    wider: { period: "week" | "month"; count: number } | null;
};

type ProgressRow = { data?: unknown; updatedAt?: string } | null | undefined;

export const ACTIVITIES: ActivityDefinition[] = [
    { id: "kana", features: ["kanaDrawCounts", "kanaListenCounts"], to: "/kana-writing-practice" },
    { id: "kanjiWriting", features: ["kanjiDrawCounts", "kanjiListenCounts"], to: "/kanji-writing-practice" },
    {
        id: "kanjiMemoryQuiz",
        features: ["kanjiMemory", "kanjiMemorySeenCounts", "kanjiMemoryGuessStats", "kanjiMemoryNotes"],
        to: "/kanji-memory-quiz",
    },
    { id: "lessons", features: ["lessonProgress", "lessonNotes", "lessonParagraphNotes"], to: "/lessons" },
    { id: "grammar", features: ["grammarProgress"], to: "/grammar" },
    { id: "vocabulary", features: ["vocabularyProgress", "vocabularyListenCounts"], to: "/vocabulary" },
    { id: "conjugation", features: ["conjugationProgress", "conjugationListenCounts"], to: "/conjugation" },
    { id: "speaking", features: ["speakingCounts"], to: "/speaking-practice" },
];

export const ACTIVITY_FEATURES = ACTIVITIES.flatMap((activity) => activity.features);

const MANY_ACTIVITIES = 4;

function hasContent(data: unknown): boolean {
    if (data === null || data === undefined) return false;
    if (typeof data === "object") return Object.keys(data as object).length > 0;
    return true;
}

export function lastActiveByActivity(rows: Record<string, ProgressRow>): Partial<Record<ActivityId, Date>> {
    const result: Partial<Record<ActivityId, Date>> = {};
    ACTIVITIES.forEach((activity) => {
        activity.features.forEach((feature) => {
            const row = rows?.[feature];
            if (!row || !hasContent(row.data) || !row.updatedAt) return;
            const time = new Date(row.updatedAt);
            if (Number.isNaN(time.getTime())) return;
            const current = result[activity.id];
            if (!current || time > current) result[activity.id] = time;
        });
    });
    return result;
}

function startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function periodStarts(now: Date): Record<ActivityPeriod, Date> {
    const today = startOfDay(now);
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    const daysSinceMonday = (today.getDay() + 6) % 7;
    const week = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceMonday);
    const month = new Date(today.getFullYear(), today.getMonth(), 1);
    return { today, yesterday, week, month };
}

function activitiesSince(lastActive: Partial<Record<ActivityId, Date>>, from: Date, until?: Date): ActivityId[] {
    return ACTIVITIES.map((activity) => activity.id)
        .filter((id) => {
            const time = lastActive[id];
            return time !== undefined && time >= from && (!until || time < until);
        })
        .sort((a, b) => (lastActive[b] as Date).getTime() - (lastActive[a] as Date).getTime());
}

export function summarizeRecentActivity(
    lastActive: Partial<Record<ActivityId, Date>>,
    now: Date = new Date()
): ActivitySummary | null {
    const starts = periodStarts(now);
    const candidates: { period: ActivityPeriod; activities: ActivityId[] }[] = [
        { period: "today", activities: activitiesSince(lastActive, starts.today) },
        { period: "yesterday", activities: activitiesSince(lastActive, starts.yesterday, starts.today) },
        { period: "week", activities: activitiesSince(lastActive, starts.week) },
        { period: "month", activities: activitiesSince(lastActive, starts.month) },
    ];
    const chosen = candidates.find((candidate) => candidate.activities.length > 0);
    if (!chosen) return null;

    const widerPeriod = chosen.period === "month" ? null : chosen.period === "week" ? "month" : "week";
    const widerCount = widerPeriod ? activitiesSince(lastActive, starts[widerPeriod]).length : 0;
    const count = chosen.activities.length;

    return {
        period: chosen.period,
        activities: chosen.activities,
        primary: ACTIVITIES.find((activity) => activity.id === chosen.activities[0]) as ActivityDefinition,
        kind: count === 1 ? "single" : count >= MANY_ACTIVITIES ? "many" : "few",
        wider: widerPeriod && widerCount > count ? { period: widerPeriod, count: widerCount } : null,
    };
}
